import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["isTabTip", "align", "as", "autoAlign", "autoAlignBoundary", "caret", "className", "children", "dropShadow", "highContrast", "onRequestClose", "open", "alignmentAxisOffset"],
  _excluded2 = ["className", "children"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import cx from 'classnames';
import PropTypes from 'prop-types';
import deprecateValuesWithin from '../../prop-types/deprecateValuesWithin.js';
import React, { useRef, useMemo, useEffect } from 'react';
import useIsomorphicEffect from '../../internal/useIsomorphicEffect.js';
import { useMergedRefs } from '../../internal/useMergedRefs.js';
import { usePrefix } from '../../internal/usePrefix.js';
import { useWindowEvent } from '../../internal/useEvent.js';
import { mapPopoverAlign } from '../../tools/mapPopoverAlign.js';
import { useFloating, offset, flip, arrow, hide, autoUpdate } from '@floating-ui/react';
import { useFeatureFlag } from '../FeatureFlags/index.js';
var PopoverContext = /*#__PURE__*/React.createContext({
  setFloating: {
    current: null
  },
  caretRef: {
    current: null
  },
  autoAlign: null
});

/**
 * Deprecated popover alignment values.
 * @deprecated Use NewPopoverAlignment instead.
 */

var Popover = /*#__PURE__*/React.forwardRef(function PopoverRenderFunction(_ref,
//this is a workaround, have to come back and fix this.
forwardRef) {
  var _popoverDimensions$cu, _cx;
  var isTabTip = _ref.isTabTip,
    _ref$align = _ref.align,
    initialAlign = _ref$align === void 0 ? isTabTip ? 'bottom-start' : 'bottom' : _ref$align,
    _ref$as = _ref.as,
    BaseComponent = _ref$as === void 0 ? 'span' : _ref$as,
    _ref$autoAlign = _ref.autoAlign,
    autoAlign = _ref$autoAlign === void 0 ? false : _ref$autoAlign,
    autoAlignBoundary = _ref.autoAlignBoundary,
    _ref$caret = _ref.caret,
    caret = _ref$caret === void 0 ? isTabTip ? false : true : _ref$caret,
    customClassName = _ref.className,
    children = _ref.children,
    _ref$dropShadow = _ref.dropShadow,
    dropShadow = _ref$dropShadow === void 0 ? true : _ref$dropShadow,
    _ref$highContrast = _ref.highContrast,
    highContrast = _ref$highContrast === void 0 ? false : _ref$highContrast,
    onRequestClose = _ref.onRequestClose,
    open = _ref.open,
    alignmentAxisOffset = _ref.alignmentAxisOffset,
    rest = _objectWithoutProperties(_ref, _excluded);
  var prefix = usePrefix();
  var floating = useRef(null);
  var caretRef = useRef(null);
  var popover = useRef(null);
  var enableFloatingStyles = useFeatureFlag('enable-v12-dynamic-floating-styles') || autoAlign;
  var align = mapPopoverAlign(initialAlign);

  // If the `Popover` is the last focusable item in the tab order, it should also close when the browser window loses focus  (#12922)
  useWindowEvent('blur', function () {
    if (open) {
      onRequestClose === null || onRequestClose === void 0 ? void 0 : onRequestClose();
    }
  });
  useWindowEvent('click', function (_ref2) {
    var _popover$current;
    var target = _ref2.target;
    if (open && target instanceof Node && !((_popover$current = popover.current) !== null && _popover$current !== void 0 && _popover$current.contains(target))) {
      onRequestClose === null || onRequestClose === void 0 ? void 0 : onRequestClose();
    }
  });

  // Slug styling places a border around the popover content so the caret
  // needs to be placed 1px further outside the popover content. To do so,
  // we look to see if any of the children has a className containing "slug"
  var initialCaretHeight = React.Children.toArray(children).some(function (x) {
    var _x$props, _x$props2;
    return (x === null || x === void 0 || (_x$props = x.props) === null || _x$props === void 0 || (_x$props = _x$props.className) === null || _x$props === void 0 ? void 0 : _x$props.includes('slug')) || (x === null || x === void 0 || (_x$props2 = x.props) === null || _x$props2 === void 0 || (_x$props2 = _x$props2.className) === null || _x$props2 === void 0 ? void 0 : _x$props2.includes('ai-label'));
  }) ? 7 : 6;
  // These defaults match the defaults defined in packages/styles/scss/components/popover/_popover.scss
  var popoverDimensions = useRef({
    offset: 10,
    caretHeight: initialCaretHeight
  });
  useIsomorphicEffect(function () {
    // The popover is only offset when a caret is present. Technically, the custom properties
    // accessed below can be set by a user even if caret=false, but doing so does not follow
    // the design specification for Popover.
    if (caret && popover.current) {
      // Gather the dimensions of the caret and prefer the values set via custom properties.
      // If a value is not set via a custom property, provide a default value that matches the
      // default values defined in the sass style file
      var getStyle = window.getComputedStyle(popover.current, null);
      var offsetProperty = getStyle.getPropertyValue('--cds-popover-offset');
      var caretProperty = getStyle.getPropertyValue('--cds-popover-caret-height');

      // Handle if the property values are in px or rem.
      // We want to store just the base number value without a unit suffix
      if (offsetProperty) {
        popoverDimensions.current.offset = offsetProperty.includes('px') ? Number(offsetProperty.split('px', 1)[0]) * 1 : Number(offsetProperty.split('rem', 1)[0]) * 16;
      }
      if (caretProperty) {
        popoverDimensions.current.caretHeight = caretProperty.includes('px') ? Number(caretProperty.split('px', 1)[0]) * 1 : Number(caretProperty.split('rem', 1)[0]) * 16;
      }
    }
  });
  var _useFloating = useFloating(enableFloatingStyles ? {
      placement: align,
      // The floating element is positioned relative to its nearest
      // containing block (usually the viewport). It will in many cases also
      // “break” the floating element out of a clipping ancestor.
      // https://floating-ui.com/docs/misc#clipping
      strategy: 'fixed',
      // Middleware order matters, arrow should be last
      middleware: [offset(!isTabTip ? {
        alignmentAxis: alignmentAxisOffset,
        mainAxis: popoverDimensions === null || popoverDimensions === void 0 || (_popoverDimensions$cu = popoverDimensions.current) === null || _popoverDimensions$cu === void 0 ? void 0 : _popoverDimensions$cu.offset
      } : 0), autoAlign && flip({
        fallbackPlacements: isTabTip ? align.includes('bottom') ? ['bottom-start', 'bottom-end', 'top-start', 'top-end'] : ['top-start', 'top-end', 'bottom-start', 'bottom-end'] : align.includes('bottom') ? ['bottom', 'bottom-start', 'bottom-end', 'right', 'right-start', 'right-end', 'left', 'left-start', 'left-end', 'top', 'top-start', 'top-end'] : ['top', 'top-start', 'top-end', 'left', 'left-start', 'left-end', 'right', 'right-start', 'right-end', 'bottom', 'bottom-start', 'bottom-end'],
        fallbackStrategy: 'initialPlacement',
        fallbackAxisSideDirection: 'start',
        boundary: autoAlignBoundary
      }), arrow({
        element: caretRef
      }), autoAlign && hide()],
      whileElementsMounted: autoUpdate
    } : {}
    // When autoAlign is turned off & the `enable-v12-dynamic-floating-styles` feature flag is not
    // enabled, floating-ui will not be used
    ),
    refs = _useFloating.refs,
    floatingStyles = _useFloating.floatingStyles,
    placement = _useFloating.placement,
    middlewareData = _useFloating.middlewareData;
  var value = useMemo(function () {
    return {
      floating: floating,
      setFloating: refs.setFloating,
      caretRef: caretRef,
      autoAlign: autoAlign
    };
  }, [refs.setFloating, autoAlign]);
  if (isTabTip) {
    var tabTipAlignments = ['bottom-start', 'bottom-end'];
    if (!tabTipAlignments.includes(align)) {
      align = 'bottom-start';
    }
  }
  useEffect(function () {
    if (enableFloatingStyles) {
      var _middlewareData$hide;
      var updatedFloatingStyles = _objectSpread(_objectSpread({}, floatingStyles), {}, {
        visibility: (_middlewareData$hide = middlewareData.hide) !== null && _middlewareData$hide !== void 0 && _middlewareData$hide.referenceHidden ? 'hidden' : 'visible'
      });
      Object.keys(updatedFloatingStyles).forEach(function (style) {
        if (refs.floating.current) {
          refs.floating.current.style[style] = updatedFloatingStyles[style];
        }
      });
      if (caret && middlewareData && middlewareData.arrow && caretRef !== null && caretRef !== void 0 && caretRef.current) {
        var _middlewareData$arrow = middlewareData.arrow,
          x = _middlewareData$arrow.x,
          y = _middlewareData$arrow.y;
        var staticSide = {
          top: 'bottom',
          right: 'left',
          bottom: 'top',
          left: 'right'
        }[placement.split('-')[0]];
        caretRef.current.style.left = x != null ? "".concat(x, "px") : '';
        caretRef.current.style.top = y != null ? "".concat(y, "px") : '';

        // Ensure the static side gets unset when flipping to other placements' axes.
        caretRef.current.style.right = '';
        caretRef.current.style.bottom = '';
        if (staticSide) {
          var _popoverDimensions$cu2;
          caretRef.current.style[staticSide] = "".concat(-(popoverDimensions === null || popoverDimensions === void 0 || (_popoverDimensions$cu2 = popoverDimensions.current) === null || _popoverDimensions$cu2 === void 0 ? void 0 : _popoverDimensions$cu2.caretHeight), "px");
        }
      }
    }
  }, [floatingStyles, refs.floating, enableFloatingStyles, middlewareData, placement, caret]);
  var ref = useMergedRefs([forwardRef, popover]);
  var currentAlignment = autoAlign && placement !== align ? placement : align;
  var className = cx((_cx = {}, _defineProperty(_cx, "".concat(prefix, "--popover-container"), true), _defineProperty(_cx, "".concat(prefix, "--popover--caret"), caret), _defineProperty(_cx, "".concat(prefix, "--popover--drop-shadow"), dropShadow), _defineProperty(_cx, "".concat(prefix, "--popover--high-contrast"), highContrast), _defineProperty(_cx, "".concat(prefix, "--popover--open"), open), _defineProperty(_cx, "".concat(prefix, "--popover--auto-align ").concat(prefix, "--autoalign"), enableFloatingStyles), _defineProperty(_cx, "".concat(prefix, "--popover--").concat(currentAlignment), true), _defineProperty(_cx, "".concat(prefix, "--popover--tab-tip"), isTabTip), _cx), customClassName);
  var mappedChildren = React.Children.map(children, function (child) {
    var _item$type;
    var item = child;
    var displayName = item === null || item === void 0 || (_item$type = item.type) === null || _item$type === void 0 ? void 0 : _item$type.displayName;

    /**
     * Only trigger elements (button) or trigger components (ToggletipButton) should be
     * cloned because these will be decorated with a trigger-specific className and ref.
     *
     * There are also some specific components that should not be cloned when autoAlign
     * is on, even if they are a trigger element.
     */
    var isTriggerElement = (item === null || item === void 0 ? void 0 : item.type) === 'button';
    var isTriggerComponent = enableFloatingStyles && displayName && ['ToggletipButton'].includes(displayName);
    var isAllowedTriggerComponent = enableFloatingStyles && !['ToggletipContent', 'PopoverContent'].includes(displayName);
    if (/*#__PURE__*/React.isValidElement(item) && (isTriggerElement || isTriggerComponent || isAllowedTriggerComponent)) {
      var _item$props;
      var _className = item === null || item === void 0 || (_item$props = item.props) === null || _item$props === void 0 ? void 0 : _item$props.className;
      var _ref3 = (item === null || item === void 0 ? void 0 : item.props).ref;
      var tabTipClasses = cx("".concat(prefix, "--popover--tab-tip__button"), _className);
      return /*#__PURE__*/React.cloneElement(item, {
        className: isTabTip && (item === null || item === void 0 ? void 0 : item.type) === 'button' ? tabTipClasses : _className || '',
        // With cloneElement, if you pass a `ref`, it overrides the original ref.
        // https://react.dev/reference/react/cloneElement#parameters
        // The block below works around this and ensures that the original ref is still
        // called while allowing the floating-ui reference element to be set as well.
        // `useMergedRefs` can't be used here because hooks can't be called from within a callback.
        // More here: https://github.com/facebook/react/issues/8873#issuecomment-489579878
        ref: function ref(node) {
          var _item$type2, _item$type3;
          // For a popover, there isn't an explicit trigger component, it's just the first child that's
          // passed in which should *not* be PopoverContent.
          // For a toggletip there is a specific trigger component, ToggletipButton.
          // In either of these cases we want to set this as the reference node for floating-ui autoAlign
          // positioning.
          if (enableFloatingStyles && (item === null || item === void 0 || (_item$type2 = item.type) === null || _item$type2 === void 0 ? void 0 : _item$type2.displayName) !== 'PopoverContent' || enableFloatingStyles && (item === null || item === void 0 || (_item$type3 = item.type) === null || _item$type3 === void 0 ? void 0 : _item$type3.displayName) === 'ToggletipButton') {
            // Set the reference element for floating-ui
            refs.setReference(node);
          }

          // Call the original ref, if any
          if (typeof _ref3 === 'function') {
            _ref3(node);
          } else if (_ref3 !== null && _ref3 !== undefined) {
            _ref3.current = node;
          }
        }
      });
    } else {
      return item;
    }
  });
  var BaseComponentAsAny = BaseComponent;
  return /*#__PURE__*/React.createElement(PopoverContext.Provider, {
    value: value
  }, /*#__PURE__*/React.createElement(BaseComponentAsAny, _extends({}, rest, {
    className: className,
    ref: ref
  }), enableFloatingStyles || isTabTip ? mappedChildren : children));
});

// Note: this displayName is temporarily set so that Storybook ArgTable
// correctly displays the name of this component
if (process.env.NODE_ENV !== 'production') {
  Popover.displayName = 'Popover';
}
function PopoverContentRenderFunction(// eslint-disable-next-line react/prop-types
_ref4, forwardRef) {
  var _cx2;
  var className = _ref4.className,
    children = _ref4.children,
    rest = _objectWithoutProperties(_ref4, _excluded2);
  var prefix = usePrefix();
  var _React$useContext = React.useContext(PopoverContext),
    setFloating = _React$useContext.setFloating,
    caretRef = _React$useContext.caretRef,
    autoAlign = _React$useContext.autoAlign;
  var ref = useMergedRefs([setFloating, forwardRef]);
  var enableFloatingStyles = useFeatureFlag('enable-v12-dynamic-floating-styles') || autoAlign;
  return /*#__PURE__*/React.createElement("span", _extends({}, rest, {
    className: "".concat(prefix, "--popover")
  }), /*#__PURE__*/React.createElement("span", {
    className: cx("".concat(prefix, "--popover-content"), className),
    ref: ref
  }, children, enableFloatingStyles && /*#__PURE__*/React.createElement("span", {
    className: cx((_cx2 = {}, _defineProperty(_cx2, "".concat(prefix, "--popover-caret"), true), _defineProperty(_cx2, "".concat(prefix, "--popover--auto-align"), true), _cx2)),
    ref: caretRef
  })), !enableFloatingStyles && /*#__PURE__*/React.createElement("span", {
    className: cx(_defineProperty({}, "".concat(prefix, "--popover-caret"), true)),
    ref: caretRef
  }));
}
var PopoverContent = /*#__PURE__*/React.forwardRef(PopoverContentRenderFunction);
PopoverContent.displayName = 'PopoverContent';
PopoverContent.propTypes = {
  /**
   * Provide elements to be rendered inside of the component
   */
  children: PropTypes.node,
  /**
   * Provide a custom class name to be added to the outermost node in the
   * component
   */
  className: PropTypes.string
};
export { Popover, PopoverContent };